import Joi from "joi";

// Schedule item validation schema (for array of schedules)
const scheduleItemSchema = Joi.object({
  day: Joi.string()
    .valid(
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    )
    .required()
    .messages({
      "any.only":
        "Day must be one of: monday, tuesday, wednesday, thursday, friday, saturday, sunday",
      "any.required": "Day is required for each schedule item",
    }),
  startTime: Joi.string()
    .pattern(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)
    .required()
    .messages({
      "string.pattern.base": "Start time must be in HH:MM format (e.g., 09:00)",
      "any.required": "Start time is required for each schedule item",
    }),
  endTime: Joi.string()
    .pattern(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)
    .required()
    .messages({
      "string.pattern.base": "End time must be in HH:MM format (e.g., 10:30)",
      "any.required": "End time is required for each schedule item",
    }),
  duration: Joi.number().min(0).max(480).optional(), // 0 to 8 hours (auto-calculated if not provided)
  isActive: Joi.boolean().optional(),
});

// Schedule array validation
const scheduleSchema = Joi.array()
  .items(scheduleItemSchema)
  .min(1)
  .max(7)
  .optional()
  .messages({
    "array.min": "At least one schedule item is required",
    "array.max": "Maximum 7 schedule items allowed (one per day)",
  });

// Settings validation schema
const settingsSchema = Joi.object({
  allowSelfEnrollment: Joi.boolean().optional(),
  requireApproval: Joi.boolean().optional(),
  isPublic: Joi.boolean().optional(),
});

// Time format: HH:MM or HH:MM:SS
const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;

// Create class validation
export const createClassValidation = (req: any, res: any, next: any) => {
  const schema = Joi.object({
    semesterId: Joi.string()
      .pattern(/^[0-9a-fA-F]{24}$/)
      .required()
      .messages({
        "string.pattern.base": "Invalid semester ID format",
        "any.required": "Semester ID is required",
      }),

    // Course identity: any ONE of courseId / className / courseName / courseCode is enough.
    courseId: Joi.string()
      .pattern(/^[0-9a-fA-F]{24}$/)
      .optional()
      .messages({
        "string.pattern.base": "Invalid course ID format",
      }),
    className: Joi.string().trim().min(1).max(100).optional().messages({
      "string.max": "Class name cannot exceed 100 characters",
    }),
    courseName: Joi.string().trim().min(1).max(100).optional().messages({
      "string.max": "Course name cannot exceed 100 characters",
    }),
    courseCode: Joi.string().trim().max(20).optional().messages({
      "string.max": "Course code cannot exceed 20 characters",
    }),
    credits: Joi.number().min(0).max(10).optional(),
    description: Joi.string().trim().max(1000).optional(),
    subject: Joi.string().trim().max(100).optional(),

    sectionCode: Joi.string().trim().min(1).max(20).optional().messages({
      "string.max": "Section code cannot exceed 20 characters",
    }),
    title: Joi.string().trim().min(2).max(150).optional(),

    // Teachers: accept single teacherId OR teacherIds array
    teacherId: Joi.string()
      .pattern(/^[0-9a-fA-F]{24}$/)
      .optional()
      .messages({
        "string.pattern.base": "Invalid teacher ID format",
      }),
    teacherIds: Joi.array()
      .items(
        Joi.string()
          .pattern(/^[0-9a-fA-F]{24}$/)
          .messages({
            "string.pattern.base": "Invalid teacher ID format",
          }),
      )
      .optional(),

    studentIds: Joi.array()
      .items(
        Joi.string()
          .pattern(/^[0-9a-fA-F]{24}$/)
          .messages({
            "string.pattern.base": "Invalid student ID format",
          }),
      )
      .optional(),
    maxStudents: Joi.number().min(1).max(200).optional(),

    // Schedule: accept either a `schedule` array OR the flat dayOfWeek+startTime+endTime form
    schedule: scheduleSchema,
    dayOfWeek: Joi.alternatives()
      .try(
        Joi.number().min(1).max(7),
        Joi.array().items(Joi.number().min(1).max(7)).min(1).max(7),
      )
      .optional(),
    startTime: Joi.string().pattern(timePattern).optional().messages({
      "string.pattern.base": "Start time must be HH:MM or HH:MM:SS",
    }),
    endTime: Joi.string().pattern(timePattern).optional().messages({
      "string.pattern.base": "End time must be HH:MM or HH:MM:SS",
    }),

    checkInStartTime: Joi.string().pattern(timePattern).optional(),
    checkInEndTime: Joi.string().pattern(timePattern).optional(),
    restroomStartTime: Joi.string().pattern(timePattern).optional(),
    restroomEndTime: Joi.string().pattern(timePattern).optional(),
    restRoomMinutes: Joi.number().min(0).max(60).optional(),

    roomNumber: Joi.string().trim().max(50).optional(),
    classRoom: Joi.string().trim().max(50).optional(),
    classRoomNumber: Joi.string().trim().max(50).optional(),
    building: Joi.string().trim().max(100).optional(),
    floor: Joi.string().trim().max(20).optional(),
    location: Joi.object({
      latitude: Joi.number().required(),
      longitude: Joi.number().required(),
      radiusMeters: Joi.number().min(0).optional(),
      name: Joi.string().trim().optional(),
    }).optional(),
    materials: Joi.array().items(Joi.object()).optional(),
    isActive: Joi.boolean().optional(),
  })
    // At least one of these must be present so we have a name for the class
    .or("className", "courseName", "courseCode", "courseId")
    // At least one teacher reference
    .or("teacherId", "teacherIds")
    // Schedule: either the array form OR the flat form (we'll convert flat → array in the controller)
    .or("schedule", "dayOfWeek")
    .messages({
      "object.missing":
        "Provide a class identity (className/courseName/courseCode/courseId), at least one teacher (teacherId/teacherIds), and a schedule (schedule[] or dayOfWeek+startTime+endTime)",
    });

  const { error, value } = schema.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};

// Update class validation
export const updateClassValidation = (req: any, res: any, next: any) => {
  const schema = Joi.object({
    semesterId: Joi.string()
      .pattern(/^[0-9a-fA-F]{24}$/)
      .optional()
      .messages({
        "string.pattern.base": "Invalid semester ID format",
      }),
    courseId: Joi.string()
      .pattern(/^[0-9a-fA-F]{24}$/)
      .optional()
      .messages({
        "string.pattern.base": "Invalid course ID format",
      }),
    sectionCode: Joi.string().trim().min(1).max(20).optional(),
    className: Joi.string().trim().min(1).max(100).optional(),
    courseName: Joi.string().trim().min(1).max(100).optional(),
    courseCode: Joi.string().trim().max(20).optional(),
    credits: Joi.number().min(0).max(10).optional(),
    title: Joi.string().trim().min(2).max(150).optional(),
    description: Joi.string().trim().max(1000).optional(),
    teacherId: Joi.string()
      .pattern(/^[0-9a-fA-F]{24}$/)
      .allow(null)
      .optional()
      .messages({
        "string.pattern.base": "Invalid teacher ID format",
      }),
    teacherIds: Joi.array()
      .items(
        Joi.string()
          .pattern(/^[0-9a-fA-F]{24}$/)
          .messages({
            "string.pattern.base": "Invalid teacher ID format",
          }),
      )
      .optional(),
    studentIds: Joi.array()
      .items(
        Joi.string()
          .pattern(/^[0-9a-fA-F]{24}$/)
          .messages({
            "string.pattern.base": "Invalid student ID format",
          }),
      )
      .optional(),
    addStudents: Joi.array()
      .items(Joi.string().pattern(/^[0-9a-fA-F]{24}$/))
      .optional(),
    removeStudents: Joi.array()
      .items(Joi.string().pattern(/^[0-9a-fA-F]{24}$/))
      .optional(),
    addTeachers: Joi.array()
      .items(Joi.string().pattern(/^[0-9a-fA-F]{24}$/))
      .optional(),
    removeTeachers: Joi.array()
      .items(Joi.string().pattern(/^[0-9a-fA-F]{24}$/))
      .optional(),
    maxStudents: Joi.number().min(1).max(200).optional(),
    subject: Joi.string().trim().max(100).optional(),
    classRoom: Joi.string().trim().max(50).allow("").optional(),
    classRoomNumber: Joi.string().trim().max(50).allow("").optional(),
    roomNumber: Joi.string().trim().max(50).allow("").optional(),
    building: Joi.string().trim().max(100).allow("").optional(),
    floor: Joi.string().trim().max(20).allow("").optional(),
    location: Joi.object({
      latitude: Joi.number().required(),
      longitude: Joi.number().required(),
      radiusMeters: Joi.number().min(0).optional(),
      name: Joi.string().trim().optional(),
    }).optional(),
    schedule: scheduleSchema.optional(),
    // Flat-form schedule (alternative to schedule[])
    dayOfWeek: Joi.alternatives()
      .try(
        Joi.number().min(1).max(7),
        Joi.array().items(Joi.number().min(1).max(7)).min(1).max(7),
      )
      .optional(),
    startTime: Joi.string().pattern(timePattern).optional(),
    endTime: Joi.string().pattern(timePattern).optional(),
    checkInStartTime: Joi.string().pattern(timePattern).optional(),
    checkInEndTime: Joi.string().pattern(timePattern).optional(),
    restroomStartTime: Joi.string().pattern(timePattern).optional(),
    restroomEndTime: Joi.string().pattern(timePattern).optional(),
    restRoomMinutes: Joi.number().min(0).max(60).optional(),
    settings: settingsSchema.optional(),
    status: Joi.string()
      .valid("active", "inactive", "completed", "cancelled")
      .optional(),
    isActive: Joi.boolean().optional(),
  });

  const { error, value } = schema.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};

// Assign teacher validation
export const assignTeacherValidation = (req: any, res: any, next: any) => {
  const schema = Joi.object({
    teacherId: Joi.string()
      .pattern(/^[0-9a-fA-F]{24}$/)
      .required()
      .messages({
        "string.pattern.base": "Invalid teacher ID format",
        "any.required": "Teacher ID is required",
      }),
  });

  const { error, value } = schema.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};

// Enroll students validation
export const enrollStudentsValidation = (req: any, res: any, next: any) => {
  const schema = Joi.object({
    studentIds: Joi.array()
      .items(
        Joi.string()
          .pattern(/^[0-9a-fA-F]{24}$/)
          .messages({
            "string.pattern.base": "Invalid student ID format",
          }),
      )
      .min(1)
      .required()
      .messages({
        "array.min": "At least one student ID is required",
        "any.required": "Student IDs array is required",
      }),
  });

  const { error, value } = schema.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};

// Update schedule validation
export const updateScheduleValidation = (req: any, res: any, next: any) => {
  const schema = Joi.object({
    schedule: scheduleSchema.required(),
  });

  const { error, value } = schema.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};

// Update settings validation
export const updateSettingsValidation = (req: any, res: any, next: any) => {
  const schema = Joi.object({
    settings: settingsSchema.required(),
  });

  const { error, value } = schema.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }

  req.body = value;
  next();
};
